import { useTranslation } from '@jq4race/i18n';
import type { LocationResponse } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

interface DeactivateLocationDialogProps {
  location: LocationResponse | null;
  /** When 'deactivate', the dialog shows a destructive confirmation.
   *  When 'activate', the dialog shows an enable confirmation. */
  action: 'deactivate' | 'activate';
  onClose: () => void;
  onConfirmed: (location: LocationResponse) => void;
}

export function DeactivateLocationDialog({
  location,
  action,
  onClose,
  onConfirmed,
}: DeactivateLocationDialogProps) {
  const { t } = useTranslation('locations');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!location) {
      setSubmitting(false);
      setError(null);
    }
  }, [location]);

  async function confirm() {
    if (!location) return;
    setSubmitting(true);
    setError(null);
    try {
      onConfirmed(location);
      onClose();
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('errors.generic') }));
    } finally {
      setSubmitting(false);
    }
  }

  const isDeactivate = action === 'deactivate';
  const title = isDeactivate ? t('dialog.deactivateTitle') : t('dialog.activateTitle');
  const description = isDeactivate
    ? t('dialog.deactivateDescription')
    : t('dialog.activateDescription');
  const confirmLabel = isDeactivate ? t('actions.deactivate') : t('actions.activate');
  const confirmingLabel = isDeactivate ? t('actions.deactivating') : t('actions.activating');

  return (
    <Dialog open={!!location} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="mt-6 rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3">
          <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
            {t('dialog.locationLabel')}
          </p>
          <p className="mt-1 break-all font-mono text-[13px] text-jq-charcoal">{location?.name}</p>
        </div>

        {error && (
          <p className="mt-4 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant={isDeactivate ? 'destructive' : 'default'}
            size="sm"
            onClick={confirm}
            disabled={submitting}
          >
            {submitting ? confirmingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
